import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle, HelpCircle, Mail, ChevronDown, ChevronUp, RefreshCcw, Database } from 'lucide-react';
import { logWarn } from '../../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => (iso || '').slice(0, 16).replace('T', ' ');

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-blue-600" />{label}</p>
    <p className="text-2xl font-bold text-[#1e2d3b] tabular-nums">{value}</p>
  </div>
);

const Transcript = ({ messages }) => (
  <div className="mt-3 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="charlie-admin-transcript">
    {messages.map((m, i) => (
      <div key={i} className={`text-sm ${m.role === 'user' ? 'text-[#1e2d3b]' : 'text-slate-600'}`}>
        <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${m.role === 'user' ? 'bg-blue-100 text-blue-700' : m.offer_contact ? 'bg-orange-100 text-orange-700' : 'bg-slate-200 text-slate-600'}`}>
          {m.role === 'user' ? 'Visitor' : m.offer_contact ? 'Charlie · unanswered' : 'Charlie'}
        </span>
        <span className="whitespace-pre-wrap">{m.content}</span>
      </div>
    ))}
  </div>
);

const SessionRow = ({ s, adminKey }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(null);
  const toggle = async () => {
    setOpen((v) => !v);
    if (!messages) {
      try {
        const r = await axios.get(`${API}/chat/admin/sessions/${encodeURIComponent(s.session_id)}`, { params: { admin_key: adminKey } });
        setMessages(r.data.session.messages || []);
      } catch (e) { logWarn('transcript load failed', e); setMessages([]); }
    }
  };
  return (
    <li className="py-3" data-testid="charlie-admin-session-row">
      <button type="button" onClick={toggle} className="flex w-full items-start gap-3 text-left" data-testid="charlie-admin-session-toggle">
        <span className="mt-0.5 text-slate-400">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-[#1e2d3b]">{s.first_question || '(no question)'}</span>
          <span className="mt-0.5 block text-xs text-slate-500">
            {fmt(s.updated_at)} · {s.first_page || '—'} · {s.questions} question{s.questions === 1 ? '' : 's'}
            {s.unanswered > 0 && <span className="ml-2 rounded bg-orange-100 px-1.5 py-0.5 font-semibold text-orange-700">{s.unanswered} unanswered</span>}
            {s.lead_email && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-700">lead: {s.lead_email}</span>}
          </span>
        </span>
      </button>
      {open && (messages ? <Transcript messages={messages} /> : <p className="mt-2 text-xs text-slate-400">Loading…</p>)}
    </li>
  );
};

export const CharlieTranscripts = ({ adminKey, days }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('unanswered');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/chat/admin/overview`, { params: { admin_key: adminKey, days } });
      setData(r.data);
    } catch (e) { logWarn('charlie overview failed', e); } finally { setLoading(false); }
  }, [adminKey, days]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const tabs = [
    { id: 'unanswered', label: `Unanswered (${t.unanswered ?? 0})` },
    { id: 'sessions', label: `Conversations (${t.sessions ?? 0})` },
    { id: 'leads', label: `Leads (${t.leads ?? 0})` },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm mb-8" data-testid="charlie-admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-bold text-[#1e2d3b] uppercase tracking-wide flex items-center gap-2"><MessageCircle className="h-4 w-4 text-blue-600" /> Charlie Echo — conversations</h3>
          <p className="text-xs text-slate-500 mt-0.5">Review what visitors ask and where the site content has gaps.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {data?.knowledge && <span className="inline-flex items-center gap-1" data-testid="charlie-admin-kb"><Database className="h-3.5 w-3.5" /> {data.knowledge.chunks} knowledge chunks</span>}
          <button type="button" onClick={load} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50" data-testid="charlie-admin-refresh">
            <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat icon={MessageCircle} label="Conversations" value={t.sessions ?? 0} testid="charlie-stat-sessions" />
        <Stat icon={HelpCircle} label="Questions" value={t.questions ?? 0} testid="charlie-stat-questions" />
        <Stat icon={HelpCircle} label="Unanswered" value={t.unanswered ?? 0} testid="charlie-stat-unanswered" />
        <Stat icon={Mail} label="Leads captured" value={t.leads ?? 0} testid="charlie-stat-leads" />
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-3">
        {tabs.map((tb) => (
          <button key={tb.id} type="button" onClick={() => setTab(tb.id)} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === tb.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`charlie-admin-tab-${tb.id}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'unanswered' && (
        <ul className="divide-y divide-slate-100" data-testid="charlie-admin-unanswered-list">
          {!data?.unanswered?.length && <li className="py-8 text-center text-sm italic text-slate-400">No unanswered questions in this period.</li>}
          {data?.unanswered?.map((u, i) => (
            <li key={`${u.session_id}-${i}`} className="py-3" data-testid="charlie-admin-unanswered-row">
              <p className="text-sm font-semibold text-[#1e2d3b]">“{u.question}”</p>
              <p className="mt-1 text-xs text-slate-600 line-clamp-2">{u.answer}</p>
              <p className="mt-1 text-xs text-slate-400">{fmt(u.created_at)} · {u.page_path || '—'}{u.lead_email && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-700">lead: {u.lead_email}</span>}</p>
            </li>
          ))}
        </ul>
      )}

      {tab === 'sessions' && (
        <ul className="divide-y divide-slate-100" data-testid="charlie-admin-sessions-list">
          {!data?.sessions?.length && <li className="py-8 text-center text-sm italic text-slate-400">No conversations in this period.</li>}
          {data?.sessions?.map((s) => <SessionRow key={s.session_id} s={s} adminKey={adminKey} />)}
        </ul>
      )}

      {tab === 'leads' && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="charlie-admin-leads-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Time</th><th className="py-2 pr-4">Email</th><th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Question</th><th className="py-2 pr-4">Page</th><th className="py-2">Emailed</th>
              </tr>
            </thead>
            <tbody>
              {!data?.leads?.length && <tr><td colSpan={6} className="py-8 text-center italic text-slate-400">No chat leads yet.</td></tr>}
              {data?.leads?.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 last:border-b-0" data-testid="charlie-admin-lead-row">
                  <td className="py-2 pr-4 whitespace-nowrap text-xs text-slate-500">{fmt(l.created_at)}</td>
                  <td className="py-2 pr-4 font-medium text-[#1e2d3b]">{l.email}</td>
                  <td className="py-2 pr-4 text-slate-700">{l.name || '—'}</td>
                  <td className="py-2 pr-4 text-slate-700 max-w-[320px] truncate" title={l.question}>{l.question}</td>
                  <td className="py-2 pr-4 text-slate-500 text-xs">{l.page_path || '—'}</td>
                  <td className="py-2 text-xs">{l.email_sent ? <span className="text-emerald-700 font-semibold">sent</span> : <span className="text-slate-400">not sent</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
